import React, { useCallback, useRef, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

const BASE_FONT_SIZE = 1.5

const ValentinesPage = () => {
  const [hasSelected, setHasSelected] = useState(
    localStorage.getItem('hasSelected') === 'true'
  )
  const [noClickCount, setNoClickCount] = useState(0)
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  const [isSpinning, setIsSpinning] = useState(false)
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null)
  const [remainingSpins, setRemainingSpins] = useState(6)
  const [wonPrizes, setWonPrizes] = useState<string[]>([])
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const prizes = [
    '盲盒一个',
    'MichilleLin大餐',
    '淘宝50刀代金券',
    '亚米50刀代金券',
    '520现金',
    '一件新衣服',
    '化妆品',
    '零食礼包',
    '三千万祝福',
    '给我挠挠',
    '神秘甜点',
    'Lady M蛋糕',
    'JellyCat公仔',
    '精致餐具',
    '小饰品',
    '一个亲亲'
  ]

  const handleYes = () => {
    localStorage.setItem('hasSelected', 'true')
    setHasSelected(true)
  }

  const handleNo = () => {
    setNoClickCount((prev) => prev + 1)
  }

  const handleSpin = useCallback(() => {
    if (isSpinning || remainingSpins <= 0) return
    setRemainingSpins((prev) => prev - 1)
    setIsSpinning(true)
    setWinnerIndex(null)

    const winnerIdx = Math.floor(Math.random() * prizes.length)
    const totalSteps = 30 + Math.floor(Math.random() * 10)
    const timeouts: ReturnType<typeof setTimeout>[] = []

    for (let i = 0; i < totalSteps; i++) {
      const delay = (() => {
        if (i < totalSteps * 0.6) return i * 60
        const slowPhaseIndex = i - Math.floor(totalSteps * 0.6)
        const slowPhaseTotal = totalSteps - Math.floor(totalSteps * 0.6)
        const baseDelay = Math.floor(totalSteps * 0.6) * 60
        let accumulated = 0
        for (let j = 0; j < slowPhaseIndex; j++) {
          accumulated += 60 + (j / slowPhaseTotal) * 300
        }
        return baseDelay + accumulated
      })()

      const currentIndex = (winnerIdx - (totalSteps - 1 - i) + prizes.length * 100) % prizes.length

      const timeout = setTimeout(() => {
        setHighlightedIndex(currentIndex)

        if (i === totalSteps - 1) {
          setWinnerIndex(winnerIdx)
          setWonPrizes((prev) => [...prev, prizes[winnerIdx]])
          setIsSpinning(false)
        }
      }, delay)

      timeouts.push(timeout)
    }

    timeoutsRef.current = timeouts
  }, [isSpinning, remainingSpins, prizes.length])

  const yesFontSize = BASE_FONT_SIZE + noClickCount * 0.8
  const noFontSize = Math.max(0.4, BASE_FONT_SIZE - noClickCount * 0.8)

  if (!hasSelected) {
    return (
      <Box
        sx={{
          background:
            'linear-gradient(135deg, #FF85A1 0%, #FFA7C4 50%, #FFCCE0 100%)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6
        }}
      >
        <Typography
          variant="h2"
          fontFamily={'ZCOOL KuaiLe'}
          align="center"
          sx={{ color: '#fff' }}
        >
          Will you be my Valentine?
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleYes}
            sx={{
              backgroundColor: '#D63384',
              fontSize: `${yesFontSize}rem`,
              px: 5,
              py: 2,
              transition: 'font-size 0.3s ease'
            }}
          >
            好的，非常愿意
          </Button>
          <Button
            variant="contained"
            onClick={handleNo}
            sx={{
              backgroundColor: '#7F8C8D',
              fontSize: `${noFontSize}rem`,
              px: 5,
              py: 2,
              transition: 'font-size 0.3s ease'
            }}
          >
            不愿意
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        background:
          'linear-gradient(135deg, #FF85A1 0%, #FFA7C4 50%, #FFCCE0 100%)',
        minHeight: '100vh',
        padding: 4
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h2"
          fontFamily={'ZCOOL KuaiLe'}
          align="center"
          sx={{ color: '#fff' }}
        >
          情人节快乐 ❤️
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Grid container spacing={2}>
          {prizes.map((prize, index) => {
            const isHighlighted = highlightedIndex === index
            const isWinner = winnerIndex === index

            return (
              <Grid size={{ xs: 6, sm: 3 }} key={index}>
                <Paper
                  elevation={isHighlighted || isWinner ? 12 : 6}
                  sx={{
                    aspectRatio: '1 / 1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    background: isWinner
                      ? 'linear-gradient(145deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)'
                      : isHighlighted
                        ? 'linear-gradient(145deg, #D63384 0%, #FF85A1 100%)'
                        : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,220,230,0.9) 100%)',
                    backdropFilter: 'blur(8px)',
                    border: isWinner
                      ? '3px solid #FFD700'
                      : isHighlighted
                        ? '3px solid #D63384'
                        : '2px solid rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    transition: 'all 0.1s ease',
                    transform: isWinner
                      ? 'scale(1.08)'
                      : isHighlighted
                        ? 'scale(1.05)'
                        : 'scale(1)',
                    boxShadow: isWinner
                      ? '0 0 30px rgba(255,215,0,0.6), 0 0 60px rgba(255,165,0,0.3)'
                      : isHighlighted
                        ? '0 0 20px rgba(214,51,132,0.5)'
                        : undefined,
                    animation: isWinner
                      ? 'winnerPulse 0.6s ease-in-out infinite alternate'
                      : undefined,
                    '@keyframes winnerPulse': {
                      '0%': {
                        boxShadow:
                          '0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,165,0,0.2)'
                      },
                      '100%': {
                        boxShadow:
                          '0 0 35px rgba(255,215,0,0.7), 0 0 70px rgba(255,165,0,0.4)'
                      }
                    },
                    '&:hover': !isSpinning
                      ? {
                          transform: 'translateY(-6px) scale(1.03)',
                          boxShadow:
                            '0 12px 28px rgba(214,51,132,0.35), 0 0 16px rgba(255,133,161,0.25)',
                          borderColor: '#D63384'
                        }
                      : {}
                  }}
                >
                  <Typography
                    fontFamily={'ZCOOL KuaiLe'}
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.15rem' },
                      color: isWinner || isHighlighted ? '#fff' : '#D63384',
                      textAlign: 'center',
                      px: 1,
                      fontWeight: isWinner ? 700 : 500,
                      textShadow: isWinner
                        ? '0 1px 4px rgba(0,0,0,0.3)'
                        : undefined
                    }}
                  >
                    {prize}
                  </Typography>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSpin}
            disabled={isSpinning || remainingSpins <= 0}
            sx={{
              fontFamily: 'ZCOOL KuaiLe',
              fontSize: '1.5rem',
              px: 6,
              py: 2,
              borderRadius: 3,
              background: isSpinning
                ? '#999'
                : 'linear-gradient(135deg, #D63384 0%, #FF85A1 100%)',
              boxShadow: '0 6px 20px rgba(214,51,132,0.4)',
              transition: 'all 0.3s ease',
              '&:hover:not(:disabled)': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 28px rgba(214,51,132,0.5)'
              }
            }}
          >
            {remainingSpins <= 0
              ? '没有次数了'
              : isSpinning
                ? '抽奖中...'
                : `开始抽奖 (${remainingSpins})`}
          </Button>
        </Box>

        {wonPrizes.length > 0 && (
          <Paper
            elevation={4}
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 4,
              background:
                'linear-gradient(145deg, rgba(255,255,255,0.92) 0%, rgba(255,228,235,0.9) 100%)',
              border: '2px solid rgba(255,255,255,0.6)'
            }}
          >
            <Typography
              fontFamily={'ZCOOL KuaiLe'}
              sx={{
                fontSize: '1.3rem',
                color: '#D63384',
                textAlign: 'center',
                mb: 2
              }}
            >
              🎁 已获得的奖品 🎁
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              {wonPrizes.map((prize, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.2,
                    borderRadius: 3,
                    background:
                      'linear-gradient(135deg, rgba(214,51,132,0.08) 0%, rgba(255,133,161,0.12) 100%)',
                    border: '1px solid rgba(214,51,132,0.15)'
                  }}
                >
                  <Typography sx={{ fontSize: '1.2rem' }}>
                    💝
                  </Typography>
                  <Typography
                    fontFamily={'ZCOOL KuaiLe'}
                    sx={{
                      fontSize: '1.1rem',
                      color: '#D63384'
                    }}
                  >
                    {prize}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default ValentinesPage
